<template>
  <!-- data-clarity-mask: ver nota en FormularioSeguro.vue -->
  <v-card class="itp-card mx-auto" max-width="760" elevation="6" data-clarity-mask="true">
    <!-- Cabecera -->
    <div class="itp-header pa-5">
      <h2 class="itp-title mb-1">{{ t.titulo }}</h2>
      <p class="itp-subtitle mb-0">{{ t.subtitulo }}</p>
    </div>

    <v-card-text class="pa-5">
      <!-- ===================== PASO 1: TIPO DE VEHÍCULO ===================== -->
      <div class="paso-label">{{ t.paso1 }}</div>
      <v-btn-toggle
        v-model="tipoVehiculo"
        mandatory
        class="mb-6 d-flex flex-wrap"
        active-class="tipo-activo"
      >
        <v-btn value="coche" class="flex-grow-1"><v-icon left>mdi-car</v-icon>{{ t.coche }}</v-btn>
        <v-btn value="moto" class="flex-grow-1"><v-icon left>mdi-motorbike</v-icon>{{ t.moto }}</v-btn>
        <v-btn value="ciclomotor" class="flex-grow-1"><v-icon left>mdi-moped</v-icon>{{ t.ciclomotor }}</v-btn>
      </v-btn-toggle>

      <!-- ===================== PASO 2: DATOS DEL VEHÍCULO ===================== -->
      <div class="paso-label">{{ t.paso2 }}</div>
      <v-row dense>
        <v-col cols="6" sm="3">
          <v-select
            v-model="mesMatriculacion"
            :items="meses"
            item-text="texto"
            item-value="valor"
            :label="t.mes"
            outlined dense
          />
        </v-col>
        <v-col cols="6" sm="3">
          <v-select
            v-model="anioMatriculacion"
            :items="anios"
            :label="t.anio"
            outlined dense
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="comunidad"
            :items="comunidades"
            :item-text="idiomaActual === 'cat' ? 'nombre_cat' : 'nombre_es'"
            item-value="id"
            :label="t.comunidad"
            outlined dense
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model.number="precioCompra"
            :label="t.precio"
            suffix="€"
            type="number"
            min="0"
            outlined dense
            :hint="t.precioHint"
            persistent-hint
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model.number="valorNuevo"
            :label="t.valorNuevo"
            suffix="€"
            type="number"
            min="0"
            outlined dense
            :hint="t.valorNuevoHint"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row dense align="center" class="mt-1">
        <v-col cols="12" sm="6" v-if="tipoVehiculo === 'coche'">
          <v-text-field
            v-model.number="caballosFiscales"
            :label="t.cvf"
            type="number"
            min="0"
            outlined dense
            :hint="t.cvfHint"
            persistent-hint
          />
        </v-col>
        <v-col cols="12" :sm="tipoVehiculo === 'coche' ? 6 : 12">
          <v-select
            v-model="tipoUso"
            :items="t.usos"
            item-text="texto"
            item-value="valor"
            :label="t.uso"
            outlined dense
          />
        </v-col>
      </v-row>

      <v-alert v-if="errorInput" type="warning" dense text class="mt-3 mb-0">
        {{ errorInput }}
      </v-alert>

      <!-- Botón calcular -->
      <div class="text-center mt-5" v-if="!mostrarFormulario && !resultadoEnviado">
        <v-btn color="primary" large class="px-8 itp-cta" @click="calcular">
          {{ t.calcular }}
        </v-btn>
      </div>

      <!-- ===================== PASO 3: DATOS DE CONTACTO (GATE) ===================== -->
      <v-expand-transition>
        <div v-if="mostrarFormulario && !resultadoEnviado">
          <v-divider class="my-5" />
          <div class="paso-label">{{ t.paso3 }}</div>
          <p class="gate-text mb-4">{{ t.gateText }}</p>

          <v-form ref="leadForm" v-model="formValido">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="lead.nombre"
                  :label="t.nombre"
                  :rules="[r.requerido]"
                  outlined dense
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="lead.telefono"
                  :label="t.telefono"
                  :rules="[r.requerido, r.telefono]"
                  outlined dense
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="lead.email"
                  :label="t.email"
                  :rules="[r.requerido, r.email]"
                  outlined dense
                />
              </v-col>
            </v-row>

            <v-checkbox
              v-model="lead.consentimiento"
              :rules="[r.aceptar]"
              class="mt-0"
              dense
            >
              <template #label>
                <span class="consent-text">{{ t.consent }}</span>
              </template>
            </v-checkbox>

              <!-- Campo trampa antispam: invisible para el usuario -->
            <input
              v-model="honeypot"
              class="gr-hp"
              type="text"
              name="_gotcha"
              tabindex="-1"
              autocomplete="off"
              aria-hidden="true"
            >

            <captcha-turnstile
              ref="captcha"
              :site-key="captchaSiteKey"
              :lang="idiomaActual === 'cat' ? 'ca' : 'es'"
              @token="onCaptchaToken"
            />

            <v-alert v-if="errorEnvio" type="error" dense text class="mb-3">
              {{ errorEnvio }}
            </v-alert>

            <div class="text-center">
              <v-btn
                color="primary"
                large
                class="px-8 itp-cta"
                :loading="enviando"
                @click="enviar"
              >
                {{ t.verResultado }}
              </v-btn>
            </div>
          </v-form>
        </div>
      </v-expand-transition>

      <!-- ===================== CONFIRMACIÓN (sin mostrar precio) ===================== -->
      <v-expand-transition>
        <div v-if="resultadoEnviado" class="mt-4">
          <div class="confirm-box pa-6 text-center">
            <v-icon size="56" color="#900A30" class="mb-3">mdi-email-check-outline</v-icon>
            <h3 class="confirm-titulo mb-2">{{ t.confirmTitulo }}</h3>
            <p class="confirm-text mb-1">
              {{ t.confirmText }} <strong>{{ lead.email }}</strong>.
            </p>
            <p class="confirm-sub mb-0">{{ t.confirmSub }}</p>
          </div>

          <div class="text-center mt-4">
            <v-btn text color="secondary" @click="reiniciar">
              <v-icon left>mdi-refresh</v-icon>{{ t.otroCalculo }}
            </v-btn>
          </div>
        </div>
      </v-expand-transition>
    </v-card-text>
  </v-card>
</template>

<script>
import captcha from '@/mixins/captcha'

export default {
  name: 'CalculadoraItp',

  mixins: [captcha],

  props: {
    // Endpoint de Formspree donde llegan los leads. Puedes reutilizar el del
    // formulario de seguros (mlgqpqkw) o crear uno nuevo para separar los avisos.
    formspreeEndpoint: {
      type: String,
      default: 'https://formspree.io/f/REEMPLAZA_ENDPOINT'
    },
    // Idioma inicial: 'es' o 'cat'. Puedes pasarlo desde la página (locale).
    idioma: { type: String, default: 'es' }
  },

  data() {
    const anioActual = new Date().getFullYear()
    return {
      idiomaActual: this.idioma === 'cat' ? 'cat' : 'es',
      tipoVehiculo: 'coche',
      mesMatriculacion: 1,
      anioMatriculacion: anioActual,
      comunidad: 'cataluna',
      precioCompra: null,
      valorNuevo: null,
      caballosFiscales: null,
      tipoUso: 'particular',

      mostrarFormulario: false,
      resultadoEnviado: false,
      enviando: false,
      errorInput: '',
      errorEnvio: '',
      formValido: false,

      lead: { nombre: '', email: '', telefono: '', consentimiento: false },
      res: {},

      // Tipos ITP 2026 por CCAA (fuente: haciendas autonómicas / BOE).
      // tipoAlto se aplica a turismos > 15 CVF en las CCAA que lo prevén.
      comunidades: [
        { id: 'galicia',      nombre_es: 'Galicia',              nombre_cat: 'Galícia',            tipo: 3 },
        { id: 'madrid',       nombre_es: 'Madrid',               nombre_cat: 'Madrid',             tipo: 4 },
        { id: 'andalucia',    nombre_es: 'Andalucía',            nombre_cat: 'Andalusia',          tipo: 4, tipoAlto: 8 },
        { id: 'aragon',       nombre_es: 'Aragón',               nombre_cat: 'Aragó',              tipo: 4 },
        { id: 'asturias',     nombre_es: 'Asturias',             nombre_cat: 'Astúries',           tipo: 4, tipoAlto: 8 },
        { id: 'baleares',     nombre_es: 'Islas Baleares',       nombre_cat: 'Illes Balears',      tipo: 4, tipoAlto: 8 },
        { id: 'murcia',       nombre_es: 'Murcia',               nombre_cat: 'Múrcia',             tipo: 4 },
        { id: 'paisvasco',    nombre_es: 'País Vasco',           nombre_cat: 'País Basc',          tipo: 4 },
        { id: 'larioja',      nombre_es: 'La Rioja',             nombre_cat: 'La Rioja',           tipo: 4 },
        { id: 'cataluna',     nombre_es: 'Cataluña',             nombre_cat: 'Catalunya',          tipo: 5 },
        { id: 'castillaleon', nombre_es: 'Castilla y León',      nombre_cat: 'Castella i Lleó',    tipo: 5, tipoAlto: 8 },
        { id: 'cantabria',    nombre_es: 'Cantabria',            nombre_cat: 'Cantàbria',          tipo: 6 },
        { id: 'clm',          nombre_es: 'Castilla-La Mancha',   nombre_cat: 'Castella-la Manxa',  tipo: 6 },
        { id: 'valenciana',   nombre_es: 'Comunidad Valenciana', nombre_cat: 'Comunitat Valenciana', tipo: 6 },
        { id: 'extremadura',  nombre_es: 'Extremadura',          nombre_cat: 'Extremadura',        tipo: 6 },
        { id: 'navarra',      nombre_es: 'Navarra',              nombre_cat: 'Navarra',            tipo: 6 },
        { id: 'canarias',     nombre_es: 'Canarias (IGIC)',      nombre_cat: 'Canàries (IGIC)',    tipo: 6.5 },
        { id: 'ceuta',        nombre_es: 'Ceuta',                nombre_cat: 'Ceuta',              tipo: 4 },
        { id: 'melilla',      nombre_es: 'Melilla',              nombre_cat: 'Melilla',            tipo: 4 }
      ],

      textos: {
        es: {
          titulo: 'Calcula el precio de la transferencia',
          subtitulo: 'Impuesto de Transmisiones Patrimoniales (ITP) + tasa DGT. Válido para todas las comunidades autónomas · Bases de cálculo BOE 2026.',
          paso1: '1. Tipo de vehículo',
          coche: 'Coche', moto: 'Moto', ciclomotor: 'Ciclomotor',
          paso2: '2. Datos del vehículo',
          mes: 'Mes matriculación', anio: 'Año matriculación',
          comunidad: 'Comunidad autónoma del comprador',
          precio: 'Precio de compraventa', precioHint: 'Lo que vas a pagar por el vehículo',
          valorNuevo: 'Valor cuando era nuevo (opcional)',
          valorNuevoHint: 'Si lo conoces, aplicamos la depreciación oficial por antigüedad',
          cvf: 'Caballos fiscales (CVF) — opcional',
          cvfHint: 'En la ficha técnica. Afecta al tipo en algunas CCAA',
          uso: 'Tipo de uso',
          usos: [
            { valor: 'particular', texto: 'Uso particular' },
            { valor: 'reducido', texto: 'Taxi, autoescuela o alquiler (reducción 30%)' }
          ],
          errPrecio: 'Introduce el precio de compraventa para continuar.',
          calcular: 'Calcular mi ITP',
          paso3: '3. Recibe el resultado por email',
          gateText: 'Introduce tus datos y te enviamos el cálculo detallado de la transferencia a tu correo. Sin compromiso.',
          nombre: 'Nombre y apellidos', telefono: 'Teléfono', email: 'Email',
          consent: 'Acepto la política de privacidad y que Gestoría Riola me contacte sobre este trámite.',
          verResultado: 'Recibir el cálculo por email',
          confirmTitulo: '¡Datos recibidos!',
          confirmText: 'Te enviaremos el cálculo detallado de la transferencia a',
          confirmSub: 'Revisa tu bandeja de entrada (y la carpeta de spam) en los próximos minutos. En Gestoría Riola confirmamos el valor fiscal y tramitamos el cambio de titularidad completo por ti.',
          otroCalculo: 'Hacer otro cálculo',
          errEnvio: 'No hemos podido enviar el cálculo. Inténtalo de nuevo o contáctanos por WhatsApp.',
          captchaEspera: 'Estamos comprobando que no eres un robot. Espera un segundo y vuelve a darle al botón.',
          rq: 'Campo obligatorio', re: 'Email no válido', rt: 'Teléfono no válido', ra: 'Debes aceptar para continuar'
        },
        cat: {
          titulo: 'Calcula el preu de la transferència',
          subtitulo: 'Impost de Transmissions Patrimonials (ITP) + taxa DGT. Vàlid per a totes les comunitats autònomes · Bases de càlcul BOE 2026.',
          paso1: '1. Tipus de vehicle',
          coche: 'Cotxe', moto: 'Moto', ciclomotor: 'Ciclomotor',
          paso2: '2. Dades del vehicle',
          mes: 'Mes matriculació', anio: 'Any matriculació',
          comunidad: 'Comunitat autònoma del comprador',
          precio: 'Preu de compravenda', precioHint: 'El que pagaràs pel vehicle',
          valorNuevo: 'Valor quan era nou (opcional)',
          valorNuevoHint: 'Si el coneixes, apliquem la depreciació oficial per antiguitat',
          cvf: 'Cavalls fiscals (CVF) — opcional',
          cvfHint: 'A la fitxa tècnica. Afecta el tipus en algunes CCAA',
          uso: "Tipus d'ús",
          usos: [
            { valor: 'particular', texto: 'Ús particular' },
            { valor: 'reducido', texto: 'Taxi, autoescola o lloguer (reducció 30%)' }
          ],
          errPrecio: 'Introdueix el preu de compravenda per continuar.',
          calcular: 'Calcular el meu ITP',
          paso3: '3. Rep el resultat per email',
          gateText: "Introdueix les teves dades i t'enviem el càlcul detallat de la transferència al teu correu. Sense compromís.",
          nombre: 'Nom i cognoms', telefono: 'Telèfon', email: 'Email',
          consent: "Accepto la política de privacitat i que Gestoria Riola em contacti sobre aquest tràmit.",
          verResultado: 'Rebre el càlcul per email',
          confirmTitulo: 'Dades rebudes!',
          confirmText: "T'enviarem el càlcul detallat de la transferència a",
          confirmSub: "Revisa la teva safata d'entrada (i la carpeta de correu brossa) en els pròxims minuts. A Gestoria Riola confirmem el valor fiscal i tramitem el canvi de titularitat complet per tu.",
          otroCalculo: 'Fer un altre càlcul',
          errEnvio: "No hem pogut enviar el càlcul. Torna-ho a provar o contacta'ns per WhatsApp.",
          captchaEspera: 'Estem comprovant que no ets un robot. Espera un segon i torna a prémer el botó.',
          rq: 'Camp obligatori', re: 'Email no vàlid', rt: 'Telèfon no vàlid', ra: "Has d'acceptar per continuar"
        }
      }
    }
  },

  computed: {
    t() { return this.textos[this.idiomaActual] },

    anios() {
      const actual = new Date().getFullYear()
      const arr = []
      for (let a = actual; a >= 1950; a--) arr.push(a)
      return arr
    },

    meses() {
      const es = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
      const cat = ['Gener','Febrer','Març','Abril','Maig','Juny','Juliol','Agost','Setembre','Octubre','Novembre','Desembre']
      const nombres = this.idiomaActual === 'cat' ? cat : es
      return nombres.map((texto, i) => ({ valor: i + 1, texto }))
    },

    r() {
      const t = this.t
      return {
        requerido: v => !!v || t.rq,
        email: v => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) || t.re,
        telefono: v => /^[0-9+\s]{6,15}$/.test(v || '') || t.rt,
        aceptar: v => !!v || t.ra
      }
    }
  },

  methods: {
    eur(n) {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency', currency: 'EUR', minimumFractionDigits: 2
      }).format(n || 0)
    },

    // Coeficiente de depreciación (Anexo IV, Orden HAC/1501/2025)
    coeficienteDepreciacion() {
      const hoy = new Date()
      const matric = new Date(this.anioMatriculacion, (this.mesMatriculacion || 1) - 1, 1)
      const anios = (hoy - matric) / (365.25 * 24 * 3600 * 1000)
      const tabla = [
        [1, 100], [2, 84], [3, 67], [4, 56], [5, 47], [6, 39],
        [7, 34], [8, 28], [9, 24], [10, 19], [11, 17], [12, 13]
      ]
      for (const [limite, pct] of tabla) if (anios < limite) return pct
      return 10
    },

    tipoDgt() {
      return this.tipoVehiculo === 'ciclomotor' ? 27.85 : 55.70
    },

    calcular() {
      this.errorInput = ''
      if (!this.precioCompra || this.precioCompra <= 0) {
        this.errorInput = this.t.errPrecio
        return
      }
      this.computar()
      this.mostrarFormulario = true
    },

    computar() {
      const com = this.comunidades.find(c => c.id === this.comunidad)

      let valorFiscal = 0
      if (this.valorNuevo && this.valorNuevo > 0) {
        const coef = this.coeficienteDepreciacion() / 100
        valorFiscal = this.valorNuevo * coef
        if (this.tipoUso === 'reducido') valorFiscal *= 0.70
      }

      const base = Math.max(valorFiscal, this.precioCompra || 0)

      let tipoPct = com.tipo
      if (
        this.tipoVehiculo === 'coche' &&
        com.tipoAlto &&
        this.caballosFiscales &&
        this.caballosFiscales > 15
      ) {
        tipoPct = com.tipoAlto
      }

      const itp = base * (tipoPct / 100)
      const tasaDgt = this.tipoDgt()

      this.res = {
        base,
        tipoPct,
        itp,
        tasaDgt,
        total: itp + tasaDgt,
        comunidadNombre: this.idiomaActual === 'cat' ? com.nombre_cat : com.nombre_es
      }
    },

    async enviar() {
      this.errorEnvio = ''

      /* Campo trampa relleno: es un bot. Fingimos que ha ido bien. */
      if (this.esSpam()) {
        this.mostrarFormulario = false
        this.resultadoEnviado = true
        return
      }

      if (!this.$refs.leadForm.validate()) return

      if (this.captchaPendiente()) {
        this.errorEnvio = this.t.captchaEspera
        return
      }

      this.computar()
      this.enviando = true

      const payload = {
        _subject: 'Nuevo cálculo ITP desde la web',
        Nombre: this.lead.nombre,
        Email: this.lead.email,
        Telefono: this.lead.telefono,
        Tipo_vehiculo: this.tipoVehiculo,
        Matriculacion: `${this.mesMatriculacion}/${this.anioMatriculacion}`,
        Comunidad: this.res.comunidadNombre,
        Precio_compraventa: this.eur(this.precioCompra),
        Valor_a_nuevo: this.valorNuevo ? this.eur(this.valorNuevo) : '(no indicado)',
        Base_imponible: this.eur(this.res.base),
        Tipo_ITP: this.res.tipoPct + ' %',
        ITP: this.eur(this.res.itp),
        Tasa_DGT: this.eur(this.res.tasaDgt),
        TOTAL_estimado: this.eur(this.res.total)
      }

      try {
        const resp = await fetch(this.formspreeEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.assign({}, payload, this.camposAntispam()))
        })
        if (!resp.ok) throw new Error('bad response')
        this.mostrarFormulario = false
        this.resultadoEnviado = true
      } catch (e) {
        this.errorEnvio = this.t.errEnvio
      } finally {
        this.enviando = false
        this.reiniciarCaptcha()
      }
    },

    reiniciar() {
      this.resultadoEnviado = false
      this.mostrarFormulario = false
      this.precioCompra = null
      this.valorNuevo = null
      this.caballosFiscales = null
      this.lead = { nombre: '', email: '', telefono: '', consentimiento: false }
      this.res = {}
    }
  },

  watch: {
    idioma(v) { this.idiomaActual = v === 'cat' ? 'cat' : 'es' }
  }
}
</script>

<style scoped>
.itp-card { border-radius: 14px; overflow: hidden; }

.itp-header { background: #900A30; color: #fff; }
.itp-title { font-size: 1.35rem; font-weight: 700; line-height: 1.2; }
.itp-subtitle { font-size: 0.85rem; opacity: 0.92; }

.paso-label { color: #900A30; font-weight: 700; font-size: 0.95rem; margin-bottom: 12px; }

.tipo-activo { background: #900A30 !important; color: #fff !important; }

.itp-cta { font-weight: 700; letter-spacing: 0.3px; }

.gate-text, .consent-text { color: #37454A; font-size: 0.9rem; }

.confirm-box {
  background: #f7f2f4;
  border: 1px solid #e6d5db;
  border-radius: 12px;
  color: #37454A;
}
.confirm-titulo { color: #900A30; font-size: 1.35rem; font-weight: 800; }
.confirm-text { font-size: 1rem; color: #37454A; }
.confirm-sub { font-size: 0.82rem; color: #6b7478; line-height: 1.45; max-width: 520px; margin: 0 auto; }

.v-btn.primary { background-color: #900A30 !important; }
</style>
